import { useEffect } from 'react';
import ReactQuill from 'react-quill';

/**
 * Image resizing for the lesson editor.
 *
 * Two halves:
 *  1. An image blot that keeps `style`/`width`/`height` on the <img>. Quill's stock image
 *     format drops unknown attributes, so without this the size is lost the moment the
 *     editor re-reads the DOM.
 *  2. Click an image to select it: a ring with corner handles plus a floating toolbar.
 *
 * Geometry note: in Quill's snow theme `.ql-editor` is the scrolling element and
 * `.ql-container` is the positioned one. The overlay lives in the container (outside the
 * editable area, so Quill never parses it) and is positioned from live bounding-rect
 * deltas, which already account for editor scroll. Listening for scroll on the container
 * would never fire — it is `.ql-editor` that scrolls.
 *
 * Widths are stored as a percentage of the editor width, so an image that fills the column
 * on a desktop still fills it on a phone. RichTextRenderer honours the stored width and
 * falls back to full width when there isn't one.
 */

const Quill = ReactQuill.Quill;
const KEPT_ATTRS = ['alt', 'height', 'width', 'style'];

/**
 * These must be added to the `formats` whitelist passed to <ReactQuill>. Quill drops any
 * format missing from that list when it parses HTML back in, so without them a saved image
 * width survives to the database but is stripped the next time the lesson is opened for
 * editing — the size silently reverts.
 */
export const IMAGE_SIZE_FORMATS = KEPT_ATTRS;

const BRAND = '#1A9983';
const MIN_PERCENT = 10;
const BAR_HEIGHT = 34;
const BAR_GAP = 8;

let formatRegistered = false;
function registerImageFormat() {
  if (formatRegistered) return;
  const BaseImage = Quill.import('formats/image');

  class SizedImage extends BaseImage {
    static formats(domNode) {
      return KEPT_ATTRS.reduce((formats, attr) => {
        if (domNode.hasAttribute(attr)) formats[attr] = domNode.getAttribute(attr);
        return formats;
      }, {});
    }

    format(name, value) {
      if (KEPT_ATTRS.indexOf(name) > -1) {
        if (value) this.domNode.setAttribute(name, value);
        else this.domNode.removeAttribute(name);
      } else {
        super.format(name, value);
      }
    }
  }

  Quill.register(SizedImage, true);
  formatRegistered = true;
}

const PRESETS = [
  { label: 'Small', width: 25 },
  { label: 'Medium', width: 50 },
  { label: 'Large', width: 75 },
  { label: 'Full', width: 100 }
];

// corner -> which way a drag grows the image
const CORNERS = [
  { key: 'nw', top: 0, left: 0, cursor: 'nwse-resize', sx: -1 },
  { key: 'ne', top: 0, left: 1, cursor: 'nesw-resize', sx: 1 },
  { key: 'sw', top: 1, left: 0, cursor: 'nesw-resize', sx: -1 },
  { key: 'se', top: 1, left: 1, cursor: 'nwse-resize', sx: 1 }
];

/**
 * @param {object}   quillRef  ref passed to <ReactQuill>
 * @param {Function} onChange  receives the editor HTML after a resize
 * @param {boolean}  enabled   false while the editor is unmounted or read-only
 */
export default function useQuillImageResize(quillRef, onChange, enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;
    registerImageFormat();

    const quill = quillRef.current && quillRef.current.getEditor();
    if (!quill) return undefined;

    const root = quill.root;                 // .ql-editor — the scroller
    const container = root.parentElement;    // .ql-container — position: relative
    if (!container) return undefined;

    let selected = null;
    let dragging = false;

    // ---- overlay ----------------------------------------------------------
    const overlay = document.createElement('div');
    overlay.className = 'ql-image-resizer';
    overlay.setAttribute('contenteditable', 'false');
    overlay.style.cssText = [
      'position:absolute',
      'display:none',
      'box-sizing:border-box',
      `outline:2px solid ${BRAND}`,
      'outline-offset:0',
      'border-radius:2px',
      'pointer-events:none',
      'z-index:20'
    ].join(';');

    const bar = document.createElement('div');
    bar.style.cssText = [
      'position:absolute',
      'display:flex',
      'align-items:center',
      'gap:2px',
      'padding:4px',
      'background:#101a20',
      'border-radius:8px',
      'pointer-events:auto',
      'box-shadow:0 6px 20px rgba(0,0,0,.28)',
      'font-family:system-ui,-apple-system,sans-serif',
      'white-space:nowrap',
      `height:${BAR_HEIGHT}px`
    ].join(';');
    // the bar must not be clipped by the editor's rounded corners / overflow
    bar.addEventListener('mousedown', (e) => e.preventDefault());

    const makeBtn = (label) => {
      const b = document.createElement('button');
      b.type = 'button';                       // never submit the lesson form
      b.textContent = label;
      b.style.cssText = [
        'border:0',
        'background:transparent',
        'color:#cfe0dd',
        'font-size:11.5px',
        'font-weight:700',
        'padding:6px 9px',
        'border-radius:5px',
        'cursor:pointer',
        'font-family:inherit',
        'line-height:1'
      ].join(';');
      b.addEventListener('mouseenter', () => { b.style.background = 'rgba(255,255,255,.12)'; b.style.color = '#fff'; });
      b.addEventListener('mouseleave', () => { b.style.background = 'transparent'; b.style.color = '#cfe0dd'; });
      return b;
    };

    const readout = document.createElement('span');
    readout.style.cssText = [
      'color:#fff',
      'background:' + BRAND,
      'font-size:11px',
      'font-weight:800',
      'padding:5px 8px',
      'border-radius:5px',
      'margin-left:2px',
      'font-variant-numeric:tabular-nums',
      'min-width:38px',
      'text-align:center'
    ].join(';');

    const handles = CORNERS.map((corner) => {
      const h = document.createElement('div');
      h.dataset.corner = corner.key;
      h.style.cssText = [
        'position:absolute',
        'width:12px',
        'height:12px',
        'background:#fff',
        `border:2px solid ${BRAND}`,
        'border-radius:3px',
        'pointer-events:auto',
        `cursor:${corner.cursor}`,
        'box-shadow:0 1px 3px rgba(0,0,0,.25)',
        corner.top ? 'bottom:-7px' : 'top:-7px',
        corner.left ? 'right:-7px' : 'left:-7px'
      ].join(';');
      overlay.appendChild(h);
      return { el: h, ...corner };
    });

    // ---- helpers ----------------------------------------------------------
    // A percentage width resolves against the editor's *content* box, so the padding has
    // to come off — otherwise a 100% image reads back as 98%.
    const editorContentWidth = () => {
      const cs = window.getComputedStyle(root);
      const inner =
        root.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
      return inner > 0 ? inner : root.clientWidth || 1;
    };

    const currentPercent = (img) => {
      const styleWidth = img.style.width;
      if (styleWidth && styleWidth.endsWith('%')) return Math.round(parseFloat(styleWidth));
      return Math.round((img.getBoundingClientRect().width / editorContentWidth()) * 100);
    };

    const position = () => {
      if (!selected || !selected.isConnected) { hide(); return; }

      const img = selected.getBoundingClientRect();
      const box = container.getBoundingClientRect();

      // scrolled out of view — hide rather than draw a stray box
      if (img.bottom < box.top || img.top > box.bottom) {
        overlay.style.display = 'none';
        return;
      }

      overlay.style.display = 'block';
      const top = img.top - box.top;
      const left = img.left - box.left;
      overlay.style.top = `${top}px`;
      overlay.style.left = `${left}px`;
      overlay.style.width = `${img.width}px`;
      overlay.style.height = `${img.height}px`;

      // Toolbar: above the image when there is room inside the container,
      // otherwise below it, otherwise tucked just inside the top edge.
      const roomAbove = top >= BAR_HEIGHT + BAR_GAP;
      const roomBelow = top + img.height + BAR_HEIGHT + BAR_GAP <= box.height;
      if (roomAbove) bar.style.top = `${-(BAR_HEIGHT + BAR_GAP)}px`;
      else if (roomBelow) bar.style.top = `${img.height + BAR_GAP}px`;
      else bar.style.top = `${BAR_GAP}px`;

      // keep the bar inside the container horizontally
      const barWidth = bar.offsetWidth || 260;
      const maxLeft = Math.max(0, box.width - left - barWidth - 4);
      bar.style.left = `${Math.min(0, maxLeft)}px`;

      readout.textContent = `${currentPercent(selected)}%`;
    };

    function hide() {
      selected = null;
      overlay.style.display = 'none';
    }

    // Quill only learns about DOM edits it made itself, so tell it to re-read, then push
    // the HTML up to the form's state.
    const commit = () => {
      quill.update('user');
      if (typeof onChange === 'function') onChange(root.innerHTML);
    };

    const applyPercent = (percent, shouldCommit = true) => {
      if (!selected) return;
      const clamped = Math.max(MIN_PERCENT, Math.min(100, Math.round(percent)));
      selected.style.width = `${clamped}%`;
      selected.removeAttribute('width');
      selected.removeAttribute('height');
      position();
      if (shouldCommit) commit();
    };

    const select = (img) => {
      selected = img;
      position();
    };

    // ---- wiring -----------------------------------------------------------
    PRESETS.forEach(({ label, width }) => {
      const btn = makeBtn(label);
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        applyPercent(width);
      });
      bar.appendChild(btn);
    });
    bar.appendChild(readout);
    overlay.appendChild(bar);
    container.appendChild(overlay);

    const onClick = (e) => {
      if (overlay.contains(e.target)) return;
      if (e.target && e.target.tagName === 'IMG' && root.contains(e.target)) select(e.target);
      else hide();
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape' && selected) hide();
    };

    let startX = 0;
    let startWidth = 0;
    let signX = 1;

    const onPointerMove = (e) => {
      if (!dragging || !selected) return;
      const nextPx = startWidth + (e.clientX - startX) * signX;
      applyPercent((nextPx / editorContentWidth()) * 100, false);   // no commit mid-drag
    };

    const onPointerUp = () => {
      if (!dragging) return;
      dragging = false;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      commit();
    };

    handles.forEach(({ el, sx }) => {
      el.addEventListener('pointerdown', (e) => {
        if (!selected) return;
        e.preventDefault();
        e.stopPropagation();
        dragging = true;
        signX = sx;
        startX = e.clientX;
        startWidth = selected.getBoundingClientRect().width;
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
      });
    });

    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeyDown);
    root.addEventListener('scroll', position);          // .ql-editor is the scroller
    window.addEventListener('resize', position);
    quill.on('text-change', position);

    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeyDown);
      root.removeEventListener('scroll', position);
      window.removeEventListener('resize', position);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      quill.off('text-change', position);
      if (overlay.parentElement) overlay.parentElement.removeChild(overlay);
    };
  }, [quillRef, onChange, enabled]);
}
